/**
 * Factory presets for ElementaryFMSynthNode (Elementary Audio FM Synthesis)
 */

export interface ElementaryFMPreset {
  name: string;
  description: string;
  params: {
    harmonicity: number;
    modulationIndex: number;
    attack: number;
    decay: number;
    sustain: number;
    release: number;
    modAttack?: number;
    modDecay?: number;
    modSustain?: number;
    modRelease?: number;
    detune?: number;
    volume?: number;
  };
}

export const ELEMENTARY_FM_PRESETS: ElementaryFMPreset[] = [
  // ─────────────────────────────────────────────────────────────
  // Classic FM Sounds
  // ─────────────────────────────────────────────────────────────
  {
    name: 'Electric Piano',
    description: 'Classic DX7-style electric piano with bell-like attack',
    params: {
      harmonicity: 3,
      modulationIndex: 14,
      attack: 0.01,
      decay: 0.8,
      sustain: 0.3,
      release: 1.2,
      modAttack: 0.01,
      modDecay: 0.5,
      modSustain: 0.2,
      modRelease: 0.8
    }
  },
  {
    name: 'FM Bass',
    description: 'Punchy FM bass with harmonic richness',
    params: {
      harmonicity: 1,
      modulationIndex: 12,
      attack: 0.001,
      decay: 0.3,
      sustain: 0.4,
      release: 0.3,
      modAttack: 0.001,
      modDecay: 0.2,
      modSustain: 0.3,
      modRelease: 0.2
    }
  },
  {
    name: 'Crystal Bell',
    description: 'Pure bell tones with long decay',
    params: {
      harmonicity: 5,
      modulationIndex: 8,
      attack: 0.001,
      decay: 2.0,
      sustain: 0.0,
      release: 3.0,
      modAttack: 0.001,
      modDecay: 1.5,
      modSustain: 0.0,
      modRelease: 2.0
    }
  },
  {
    name: 'Brass Stab',
    description: 'Bright, brassy FM sound for stabs',
    params: {
      harmonicity: 2,
      modulationIndex: 20,
      attack: 0.02,
      decay: 0.2,
      sustain: 0.7,
      release: 0.3,
      modAttack: 0.01,
      modDecay: 0.1,
      modSustain: 0.8,
      modRelease: 0.2
    }
  },
  {
    name: 'Organ',
    description: 'Full-bodied FM organ with harmonic overtones',
    params: {
      harmonicity: 2,
      modulationIndex: 6,
      attack: 0.01,
      decay: 0.1,
      sustain: 0.9,
      release: 0.3,
      modAttack: 0.01,
      modDecay: 0.1,
      modSustain: 0.9,
      modRelease: 0.3
    }
  },

  // ─────────────────────────────────────────────────────────────
  // Metallic / Inharmonic
  // ─────────────────────────────────────────────────────────────
  {
    name: 'Marimba',
    description: 'Wooden mallet percussion with quick decay',
    params: {
      harmonicity: 4,
      modulationIndex: 10,
      attack: 0.001,
      decay: 0.5,
      sustain: 0.0,
      release: 0.5,
      modAttack: 0.001,
      modDecay: 0.3,
      modSustain: 0.0,
      modRelease: 0.3
    }
  },
  {
    name: 'Metallic',
    description: 'Inharmonic metallic tones, gong-like',
    params: {
      harmonicity: 1.414,
      modulationIndex: 25,
      attack: 0.001,
      decay: 1.5,
      sustain: 0.1,
      release: 2.0,
      modAttack: 0.001,
      modDecay: 1.0,
      modSustain: 0.1,
      modRelease: 1.5
    }
  },
  {
    name: 'Chime',
    description: 'Tubular bell chime, bright and resonant',
    params: {
      harmonicity: 3.5,
      modulationIndex: 15,
      attack: 0.001,
      decay: 2.5,
      sustain: 0.0,
      release: 3.0,
      modAttack: 0.001,
      modDecay: 2.0,
      modSustain: 0.0,
      modRelease: 2.5
    }
  },

  // ─────────────────────────────────────────────────────────────
  // Leads & Pads
  // ─────────────────────────────────────────────────────────────
  {
    name: 'FM Lead',
    description: 'Cutting lead sound with FM brightness',
    params: {
      harmonicity: 3,
      modulationIndex: 18,
      attack: 0.01,
      decay: 0.2,
      sustain: 0.7,
      release: 0.4,
      modAttack: 0.01,
      modDecay: 0.15,
      modSustain: 0.6,
      modRelease: 0.3
    }
  },
  {
    name: 'Soft Lead',
    description: 'Gentle FM lead with lower modulation',
    params: {
      harmonicity: 2,
      modulationIndex: 5,
      attack: 0.05,
      decay: 0.3,
      sustain: 0.6,
      release: 0.5,
      modAttack: 0.03,
      modDecay: 0.2,
      modSustain: 0.5,
      modRelease: 0.4
    }
  },
  {
    name: 'FM Pad',
    description: 'Evolving FM pad with slow attack',
    params: {
      harmonicity: 2,
      modulationIndex: 8,
      attack: 0.8,
      decay: 0.5,
      sustain: 0.7,
      release: 2.0,
      modAttack: 0.5,
      modDecay: 0.3,
      modSustain: 0.6,
      modRelease: 1.5
    }
  },
  {
    name: 'Glass Pad',
    description: 'Crystalline FM pad with high harmonicity',
    params: {
      harmonicity: 5,
      modulationIndex: 4,
      attack: 1.0,
      decay: 0.5,
      sustain: 0.6,
      release: 2.5,
      modAttack: 0.8,
      modDecay: 0.3,
      modSustain: 0.5,
      modRelease: 2.0
    }
  }
];

export function getElementaryFMPreset(name: string): ElementaryFMPreset | undefined {
  return ELEMENTARY_FM_PRESETS.find(p => p.name === name);
}

export function getElementaryFMPresetNames(): string[] {
  return ELEMENTARY_FM_PRESETS.map(p => p.name);
}
